import styled from '@emotion/styled';
import { layout, palette, radius, shadow, spacing, transition, typography } from '../../../../style-utils/theme';
import { touch, up } from '../../../../style-utils/media-queries';

/* A hotel's reception (Independent Stays IS9.6): tablet first, a staff
   surface on the property's clock. Tokens only. */

export const Page = styled.main`
  min-height: 100vh;
  min-height: 100dvh;
  background: ${palette.porcelain};
  padding: ${spacing.sm} ${layout.gutterMobile} ${layout.fabReserve};
  &:focus {
    outline: none;
  }
  ${up('md')} {
    padding: ${spacing.md} ${layout.gutterTablet} ${layout.fabReserve};
  }
  ${up('lg')} {
    padding: ${spacing.lg} ${layout.gutter} ${layout.fabReserve};
  }
`;

export const Inner = styled.div`
  width: 100%;
  max-width: ${layout.contentMaxWidth};
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
`;

export const Head = styled.header`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.sm};
  padding-right: ${layout.bellDockReserve};
`;

export const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  min-width: 0;
`;

export const Title = styled.h1`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xl}px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.tight};
  line-height: ${typography.leading.snug};
  color: ${palette.ink};
  overflow-wrap: anywhere;
`;

export const Sub = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
  overflow-wrap: anywhere;
`;

export const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: ${spacing.sm};
`;

export const Tabs = styled.div`
  display: flex;
  gap: ${spacing.half};
  padding: ${spacing.half};
  background: ${palette.surfaceMuted};
  border-radius: ${radius.sm};
  width: 100%;
  overflow-x: auto;
  ${up('md')} {
    width: auto;
  }
`;

export const TabButton = styled.button<{ $active: boolean }>`
  flex: 1 0 auto;
  min-height: ${layout.controlHeight};
  padding: 0 ${spacing.sm};
  border: 1px solid ${({ $active }) => ($active ? palette.line : 'transparent')};
  border-radius: ${radius.xs};
  background: ${({ $active }) => ($active ? palette.surface : 'transparent')};
  box-shadow: ${({ $active }) => ($active ? shadow.xs : 'none')};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  color: ${({ $active }) => ($active ? palette.ink : palette.slate)};
  cursor: pointer;
  transition: ${transition.colors};
  &:hover {
    color: ${palette.ink};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

export const Counts = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.xs};
`;

export const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
`;

export const Row = styled.li`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${spacing.sm};
  padding: ${spacing.sm};
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  box-shadow: ${shadow.sm};
  ${up('md')} {
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: start;
    padding: ${spacing.md};
  }
`;

export const RowMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  min-width: 0;
`;

export const Name = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.md}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
  overflow-wrap: anywhere;
`;

export const Meta = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.slate};
  overflow-wrap: anywhere;
`;

export const Pills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.xs};
`;

export const People = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
`;

export const Person = styled.li`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.xs};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.inkSoft};
  min-width: 0;
`;

export const RowActs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.xs};
  ${up('md')} {
    flex-direction: column;
    align-items: stretch;
    min-width: 176px;
  }
`;

export const Empty = styled.p`
  margin: 0;
  padding: ${spacing.md};
  background: ${palette.surface};
  border: 1px dashed ${palette.lineStrong};
  border-radius: ${radius.md};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.slate};
  text-align: center;
`;

export const RoomGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(88px, 1fr));
  gap: ${spacing.xs};
`;

export const RoomChoice = styled.button<{ $checked: boolean }>`
  min-height: ${layout.touchTarget};
  padding: ${spacing.xs};
  border: 1px solid ${({ $checked }) => ($checked ? palette.lagoon : palette.line)};
  border-radius: ${radius.xs};
  background: ${({ $checked }) => ($checked ? palette.tealTint : palette.surface)};
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
  cursor: pointer;
  transition: ${transition.colors};
  &:hover {
    border-color: ${palette.lagoon};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
  ${touch} {
    font-size: ${typography.size.base}px;
  }
`;

export const QrBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacing.sm};
  padding: ${spacing.md};
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  & img {
    width: 100%;
    max-width: 280px;
    height: auto;
  }
`;

export const Inline = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.xs};
  justify-content: flex-end;
`;
